/**
 * Job-scoped session draft for Apply with Ari (TP-498).
 * Key: twine_apply_ari_draft_{briefId}
 * Answers live client-side until email → passwordless auth; then
 * `/projects/:id/apply-ari/complete` persists profile + sends pitch.
 */

import {
  buildSignupAriPersistPayload,
  buildSignupAriLocationLabel,
} from 'helpers/signupAriDraft';

export const APPLY_ARI_DRAFT_KEY_PREFIX = 'twine_apply_ari_draft_';
export const APPLY_ARI_PENDING_SEARCH_KEY = 'twine_apply_ari_pending_search';

export const APPLY_ARI_STEPS = [
  'name',
  'country',
  'city',
  'roles',
  'experience',
  'bio',
  'travel',
  'fee',
  'pitchAsk',
  'pitch',
  'email',
];

const EMPTY_ANSWERS = {
  name: '',
  country: '',
  countryCode: '',
  city: '',
  roles: [],
  experience: [],
  bio: '',
  travel: null,
  fee: null,
  feeAmount: 0,
  pitchAsk: null,
  pitch: '',
  email: '',
};

function canUseSessionStorage() {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.sessionStorage);
  } catch (e) {
    return false;
  }
}

/**
 * @param {string|number} briefId
 * @returns {string}
 */
export function getApplyAriDraftKey(briefId) {
  return `${APPLY_ARI_DRAFT_KEY_PREFIX}${String(briefId || '').trim()}`;
}

/**
 * Guest chat path for a job.
 * @param {string} routeId - URL id (encoded slug or numeric)
 */
export function getApplyAriPath(routeId) {
  return `/projects/${routeId}/apply-ari`;
}

/**
 * Auth-only complete path after passwordless verify.
 * @param {string} routeId
 */
export function getApplyAriCompletePath(routeId) {
  return `/projects/${routeId}/apply-ari/complete`;
}

export function getEmptyApplyAriAnswers() {
  return {
    ...EMPTY_ANSWERS,
    roles: [],
    experience: [],
    travel: null,
    fee: null,
    feeAmount: 0,
    pitchAsk: null,
  };
}

/**
 * @param {string|number} briefId
 * @returns {{ answers: Object, activeStep: string, completedSteps: string[] } | null}
 */
export function loadApplyAriDraft(briefId) {
  if (!canUseSessionStorage() || !briefId) return null;
  try {
    const raw = window.sessionStorage.getItem(getApplyAriDraftKey(briefId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      answers: {
        ...getEmptyApplyAriAnswers(),
        ...(parsed.answers && typeof parsed.answers === 'object'
          ? parsed.answers
          : {}),
      },
      activeStep:
        typeof parsed.activeStep === 'string' &&
        APPLY_ARI_STEPS.includes(parsed.activeStep)
          ? parsed.activeStep
          : 'name',
      completedSteps: Array.isArray(parsed.completedSteps)
        ? parsed.completedSteps.filter((id) => APPLY_ARI_STEPS.includes(id))
        : [],
    };
  } catch (e) {
    return null;
  }
}

/**
 * @param {string|number} briefId
 * @param {{ answers: Object, activeStep: string, completedSteps: string[] }} draft
 */
export function saveApplyAriDraft(briefId, draft) {
  if (!canUseSessionStorage() || !briefId || !draft) return;
  try {
    window.sessionStorage.setItem(
      getApplyAriDraftKey(briefId),
      JSON.stringify({
        answers: { ...getEmptyApplyAriAnswers(), ...(draft.answers || {}) },
        activeStep: draft.activeStep || 'name',
        completedSteps: Array.isArray(draft.completedSteps)
          ? draft.completedSteps
          : [],
        savedAt: Date.now(),
      })
    );
  } catch (e) {
    // Ignore quota / private mode failures — chat still works in memory.
  }
}

/**
 * @param {string|number} briefId
 */
export function clearApplyAriDraft(briefId) {
  if (!canUseSessionStorage() || !briefId) return;
  try {
    window.sessionStorage.removeItem(getApplyAriDraftKey(briefId));
  } catch (e) {
    // ignore
  }
}

/** Mark that `/ari` should auto-run a search once after Launch Ari from summary. */
export function markApplyAriPendingSearch() {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.setItem(APPLY_ARI_PENDING_SEARCH_KEY, '1');
  } catch (e) {
    // ignore
  }
}

export function hasApplyAriPendingSearch() {
  if (!canUseSessionStorage()) return false;
  try {
    return window.sessionStorage.getItem(APPLY_ARI_PENDING_SEARCH_KEY) === '1';
  } catch (e) {
    return false;
  }
}

/**
 * Read and clear the pending auto-search flag (one-shot).
 * @returns {boolean}
 */
export function consumeApplyAriPendingSearch() {
  if (!canUseSessionStorage()) return false;
  try {
    const pending =
      window.sessionStorage.getItem(APPLY_ARI_PENDING_SEARCH_KEY) === '1';
    if (pending) {
      window.sessionStorage.removeItem(APPLY_ARI_PENDING_SEARCH_KEY);
    }
    return pending;
  } catch (e) {
    return false;
  }
}

/**
 * Profile + Ari criteria from apply answers (reuses signup payload builder).
 * Bio is added to profile when present.
 * @param {Object} answers
 */
export function buildApplyAriPersistPayload(answers = {}) {
  const base = buildSignupAriPersistPayload({
    ...answers,
    // Apply flow skips newsletter — leave unset so prefs are not forced.
    newsletter: null,
  });
  const bio = String(answers.bio || '').trim();
  if (bio) {
    base.profile.bio = bio;
  }
  return base;
}

export { buildSignupAriLocationLabel };
